import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  deleteInventoryStockLocation,
  saveInventoryStockLocation,
  setDefaultInventoryStockLocation,
} from '@/renderer/services/inventoryStockLocationService';
import { SaveInventoryStockLocationRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveInventoryStockLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveInventoryStockLocationRequest) => {
      const result = await saveInventoryStockLocation(payload);
      if (!result.success) {
        throw new Error(
          result.error ?? 'Failed to save inventory stock location',
        );
      }

      return result.data!;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess(
        payload.id ? 'Stock location updated.' : 'Stock location created.',
      );
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useSetDefaultInventoryStockLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationId: string) => {
      const result = await setDefaultInventoryStockLocation(locationId);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to set default stock location');
      }

      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess('Default stock location updated.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useDeleteInventoryStockLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      outletId,
      locationId,
    }: {
      companyId: number;
      outletId: string;
      locationId: string;
    }) => {
      const result = await deleteInventoryStockLocation(
        companyId,
        outletId,
        locationId,
      );
      if (!result.success) {
        throw new Error(
          result.error ?? 'Failed to delete inventory stock location',
        );
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess('Stock location deleted.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}
